import { useCallback, useEffect } from "react";
import { create } from "zustand";
import en from "../locales/en.json";
import ru from "../locales/ru.json";

const DICTS: Record<string, Record<string, string>> = { en, ru };
const SUPPORTED = ["en", "ru"] as const;
export type Locale = (typeof SUPPORTED)[number];

const COOKIE_NAME = "lang";
const FALLBACK: Locale = "en";

function readLocale(): Locale {
  if (typeof document === "undefined") return FALLBACK;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`));
  const raw = match
    ? decodeURIComponent(match[1])
    : (typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : FALLBACK);
  return (SUPPORTED as readonly string[]).includes(raw) ? (raw as Locale) : FALLBACK;
}

function writeLocale(locale: Locale): void {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(locale)};path=/;max-age=31536000;samesite=lax`;
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : `{${key}}`,
  );
}

// Global locale store so every consumer of useLocale subscribes to the same
// state. Previously a per-component useState meant switching language in the
// sidebar updated only the sidebar — every other component kept its own copy.
interface LocaleStore {
  locale: Locale;
  setLocale: (next: Locale) => void;
}

const useLocaleStore = create<LocaleStore>((set) => ({
  locale: readLocale(),
  setLocale: (next) => {
    writeLocale(next);
    set({ locale: next });
  },
}));

export function useLocale() {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const dict = DICTS[locale] ?? en;
      const raw = dict[key] ?? (en as Record<string, string>)[key] ?? key;
      return interpolate(raw, params);
    },
    [locale],
  );

  // tOr: like t() but falls back to a different key when neither current locale
  // nor English has the requested key. Useful for dynamic message codes
  // (e.g. error_code from the backend).
  const tOr = useCallback(
    (key: string, fallbackKey: string, params?: Record<string, string | number>) => {
      const has = (k: string) =>
        Object.prototype.hasOwnProperty.call(DICTS[locale] ?? {}, k) ||
        Object.prototype.hasOwnProperty.call(en, k);
      return t(has(key) ? key : fallbackKey, params);
    },
    [locale, t],
  );

  return { locale, setLocale, t, tOr, locales: SUPPORTED };
}
